import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import useOverwatchAccess from '../../hooks/useOverwatchAccess';
import { API_URL } from '../../utils/api';
import SusScoreBadge from './SusScoreBadge';
import './Overwatch.css';

/**
 * Full overwatch profile for a single player. Shows the aggregate rolling
 * rating, baseline statistics, and every suspicion report ever generated for
 * the player. Used by reviewers to spot patterns across matches — one bad
 * match is noise, a trend is a signal.
 */
export default function OverwatchPlayer() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { steamId } = useParams();
  const { hasAccess, loading: accessLoading } = useOverwatchAccess();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/overwatch/player/${steamId}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [steamId, getAuthHeaders]);

  useEffect(() => {
    if (!accessLoading && !hasAccess) {
      navigate('/', { replace: true });
      return;
    }
    if (hasAccess) load();
  }, [accessLoading, hasAccess, navigate, load]);

  const chartData = useMemo(() => {
    if (!data || !data.reports || data.reports.length === 0) return [];
    return [...data.reports]
      .reverse()
      .map((r) => ({
        date: r.analyzed_at ? new Date(r.analyzed_at).toLocaleDateString('sv-SE') : '?',
        score: r.sus_score,
        map: r.map_name || t('overwatch.mapFallback', { n: r.map_number }),
        match: r.match_id,
      }));
  }, [data, t]);

  if (accessLoading || !hasAccess || loading) return null;
  if (!data) return <div className="overwatch-empty">{t('overwatch.noData')}</div>;

  const baseline = data.baseline;
  const aggregate = data.aggregate || {};
  const displayName = data.player_name || data.steam_id;

  return (
    <div className="overwatch-page">
      <button className="overwatch-back-btn" onClick={() => navigate(-1)}>
        ← {t('profile.back')}
      </button>

      <h1>{displayName}</h1>
      <p className="overwatch-subtitle">
        {t('overwatch.playerProfile')} — {data.steam_id}
      </p>

      <div className="overwatch-report-detail">
        <h2 className="overwatch-section-heading">
          {t('overwatch.suspicionHistory')}
          <span className="overwatch-section-subtitle">
            {t('overwatch.avgSuspicionBasedOn', { count: aggregate.matches_analyzed || 0 })}
          </span>
        </h2>
        <div className="overwatch-report-meta">
          <div>
            <div className="label">{t('overwatch.avgScore')}</div>
            <div className="value">
              <SusScoreBadge score={aggregate.rolling_avg_sus_score || 0} />
            </div>
          </div>
          <div>
            <div className="label">{t('overwatch.analyzedMatches')}</div>
            <div className="value">{aggregate.matches_analyzed || 0}</div>
          </div>
          <div>
            <div className="label">{t('overwatch.totalReports')}</div>
            <div className="value">{data.reports?.length || 0}</div>
          </div>
        </div>
        <h3>{t('overwatch.flagFrequency')}</h3>
        <div className="flag-chips">
          {Object.entries(aggregate.flag_totals || {})
            .sort((a, b) => b[1] - a[1])
            .map(([f, c]) => <span key={f} className="flag-chip">{f} ×{c}</span>)}
          {Object.keys(aggregate.flag_totals || {}).length === 0 && (
            <span className="overwatch-muted-text">{t('overwatch.noFlags')}</span>
          )}
        </div>
      </div>

      {chartData.length > 1 && (
        <div className="overwatch-report-detail">
          <h2 className="overwatch-section-heading">
            {t('overwatch.scoreTrend')}
            <span className="overwatch-section-subtitle">
              {t('overwatch.scoreTrendSub')}
            </span>
          </h2>
          <div className="overwatch-chart-container">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2b2d31" />
                <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis domain={[0, 10]} tick={{ fill: '#888', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#1c1e21', border: '1px solid #333', borderRadius: 6 }}
                  labelStyle={{ color: '#e5e5e5' }}
                  formatter={(value, name, props) => [
                    `${value.toFixed(1)} — ${props.payload.map} (match #${props.payload.match})`,
                    t('overwatch.colScore'),
                  ]}
                />
                <ReferenceLine y={3} stroke="#e0c659" strokeDasharray="5 3" label={{ value: t('overwatch.refLineInvestigate'), fill: '#e0c659', fontSize: 10 }} />
                <ReferenceLine y={6} stroke="#e09659" strokeDasharray="5 3" label={{ value: t('overwatch.refLineSuspicion'), fill: '#e09659', fontSize: 10 }} />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#6ea8fe"
                  strokeWidth={2}
                  dot={{ fill: '#6ea8fe', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {baseline && (
        <div className="overwatch-report-detail">
          <h2 className="overwatch-section-heading">
            {t('overwatch.baselineHeading')}
            <span className="overwatch-section-subtitle">
              {t('overwatch.baselineSub', { count: baseline.matches_analyzed })}
            </span>
          </h2>
          <div className="overwatch-report-meta">
            <div>
              <div className="label">{t('overwatch.avgHs')}</div>
              <div className="value">{((baseline.avg_headshot_pct || 0) * 100).toFixed(1)}%</div>
            </div>
            <div>
              <div className="label">{t('overwatch.spreadHs')}</div>
              <div className="value overwatch-metric-with-help">
                {((baseline.stddev_headshot_pct || 0) * 100).toFixed(1)}%
                <span className="overwatch-metric-help">
                  {t('overwatch.stddevHsHelp')}
                </span>
              </div>
            </div>
            <div>
              <div className="label">{t('overwatch.avgKd')}</div>
              <div className="value">{(baseline.avg_kd || 0).toFixed(2)}</div>
            </div>
            <div>
              <div className="label">{t('overwatch.spreadKd')}</div>
              <div className="value overwatch-metric-with-help">
                {(baseline.stddev_kd || 0).toFixed(2)}
                <span className="overwatch-metric-help">
                  {t('overwatch.stddevKdHelp')}
                </span>
              </div>
            </div>
            <div>
              <div className="label">{t('overwatch.avgAdr')}</div>
              <div className="value">{(baseline.avg_adr || 0).toFixed(0)}</div>
            </div>
            <div>
              <div className="label">{t('overwatch.spreadAdr')}</div>
              <div className="value overwatch-metric-with-help">
                {(baseline.stddev_adr || 0).toFixed(0)}
                <span className="overwatch-metric-help">
                  {t('overwatch.stddevAdrHelp')}
                </span>
              </div>
            </div>
            <div>
              <div className="label">{t('overwatch.openingKillRate')}</div>
              <div className="value">{((baseline.avg_opening_kill_rate || 0) * 100).toFixed(1)}%</div>
            </div>
            <div>
              <div className="label">{t('overwatch.multikillRate')}</div>
              <div className="value">{((baseline.avg_multikill_rate || 0) * 100).toFixed(1)}%</div>
            </div>
          </div>
          {baseline.matches_analyzed < 5 && (
            <p className="overwatch-muted-text">
              {t('overwatch.fewMatchesNote')}
            </p>
          )}
        </div>
      )}

      <h2>{t('overwatch.reportsCount', { count: data.reports?.length || 0 })}</h2>
      {data.reports && data.reports.length > 0 ? (
        <table className="overwatch-table">
          <thead>
            <tr>
              <th>{t('overwatch.colScore')}</th>
              <th>{t('nav.match')}</th>
              <th>{t('overwatch.colMap')}</th>
              <th>{t('overwatch.colFlags')}</th>
              <th>{t('overwatch.colStatus')}</th>
              <th>{t('overwatch.colDate')}</th>
            </tr>
          </thead>
          <tbody>
            {data.reports.map((r) => (
              <tr key={r.id} onClick={() => navigate(`/overwatch/report/${r.id}`)}>
                <td><SusScoreBadge score={r.sus_score} /></td>
                <td>#{r.match_id}</td>
                <td>{r.map_name || t('overwatch.mapFallback', { n: r.map_number })}</td>
                <td>
                  <div className="flag-chips">
                    {(r.flags || []).map((f) => (
                      <span key={f} className="flag-chip">{f}</span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className={`review-status ${r.review_status}`}>{r.review_status}</span>
                </td>
                <td>{r.analyzed_at ? new Date(r.analyzed_at).toLocaleDateString('sv-SE') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="overwatch-empty">{t('overwatch.noReportsYet')}</div>
      )}
    </div>
  );
}
